import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { withStyles, Button, CircularProgress, Typography } from '@material-ui/core';
import FolderOpen from '@material-ui/icons/FolderOpen';
import EmptyState from 'components/shared/EmptyState';
import { getCottageName, sortCottagesByName, DEFAULT_MAX_FILES_PER_SLOT } from '../utils';
import FileGroupAccordion from './FileGroupAccordion';
import MultiUploadDialog from './MultiUploadDialog';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  topSection: {
    marginBottom: theme.spacing(3),
  },
  topDescription: {
    marginBottom: theme.spacing(2),
  },
  uploadButton: {
    marginBottom: theme.spacing(1),
  },
  allowedTypes: {
    color: theme.palette.grey[500],
    fontSize: '0.75rem',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
});

const HostFilesList = ({
  classes,
  config,
  files,
  counts,
  groupedFiles,
  cottages,
  isLoading,
  isUploading,
  uploadProgress,
  uploadFileIndex,
  uploadFileCount,
  isUpdating,
  onUpload,
  onView,
  onDownload,
  onRename,
  onReassign,
  onDelete,
}) => {
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const uploadDialogRef = React.useRef(null);
  const handleUploadClose = React.useCallback(() => setUploadOpen(false), []);
  const handleUploadOpen = React.useCallback(() => {
    setUploadOpen(true);
    if (cottages?.length === 1) {
      uploadDialogRef.current?.openPicker();
    }
  }, [cottages]);

  const sortedCottages = React.useMemo(() => sortCottagesByName(cottages), [cottages]);

  if (isLoading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (!cottages || cottages.length === 0) {
    return (
      <EmptyState
        icon={FolderOpen}
        title={<FormattedMessage id="hostFiles.notAvailable" defaultMessage="Not available" />}
        description={
          <FormattedMessage
            id="hostFiles.notAvailableDescription"
            defaultMessage="This feature is available for hosts with listings."
          />
        }
      />
    );
  }

  const hasFiles = files && files.length > 0;
  const maxPerSlot = config.max_files_per_slot ?? DEFAULT_MAX_FILES_PER_SLOT;

  return (
    <div className={classes.root}>
      <div className={classes.topSection}>
        <Typography variant="body1" className={classes.topDescription}>
          <FormattedMessage
            id="hostFiles.topDescription"
            defaultMessage="Save files you often send to guests -- e.g. welcome letters, directions or house rules."
          />
        </Typography>
        <Button
          variant="contained"
          color="primary"
          className={classes.uploadButton}
          onClick={handleUploadOpen}
        >
          <FormattedMessage id="hostFiles.upload" defaultMessage="Upload file" />
        </Button>
        <Typography className={classes.allowedTypes}>
          <FormattedMessage id="hostFiles.allowedTypes" defaultMessage="Allowed file types" />
        </Typography>
        <Typography className={classes.allowedTypes}>
          <FormattedMessage
            id="hostFiles.allowedTypesList"
            defaultMessage="PDF, TXT, JPG, PNG, WEBP, HEIC"
          />
        </Typography>
      </div>

      {!hasFiles ? (
        <EmptyState
          icon={FolderOpen}
          title={<FormattedMessage id="hostFiles.noFiles" defaultMessage="No files yet" />}
          description={
            <FormattedMessage
              id="hostFiles.noFilesDescription"
              defaultMessage="Upload a file you can quickly attach to your messages."
            />
          }
        />
      ) : (
        <>
          {sortedCottages.map((cottage) => {
            const cottageCount = (counts.listings && counts.listings[cottage.id]) || 0;
            const cottageFiles = groupedFiles[cottage.id] || [];
            const cottageName = getCottageName(cottage);
            return (
              <FileGroupAccordion
                key={cottage.id}
                cottageId={cottage.id}
                cottageName={cottageName}
                label={`#${cottage.id} - ${cottageName}`}
                count={cottageCount}
                maxCount={maxPerSlot}
                files={cottageFiles}
                cottages={cottages}
                counts={counts}
                isUpdating={isUpdating}
                onView={onView}
                onDownload={onDownload}
                onRename={onRename}
                onReassign={onReassign}
                onDelete={onDelete}
                defaultExpanded={cottageFiles.length > 0}
              />
            );
          })}
        </>
      )}

      <MultiUploadDialog
        ref={uploadDialogRef}
        open={uploadOpen}
        config={config}
        cottages={cottages}
        counts={counts}
        maxCount={maxPerSlot}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        uploadFileIndex={uploadFileIndex}
        uploadFileCount={uploadFileCount}
        onUpload={onUpload}
        onClose={handleUploadClose}
      />
    </div>
  );
};

HostFilesList.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  config: PropTypes.shape({
    allowed_mime_types: PropTypes.arrayOf(PropTypes.string),
    max_file_size_bytes: PropTypes.number,
    max_files_per_slot: PropTypes.number,
  }),
  files: PropTypes.arrayOf(PropTypes.shape({})),
  counts: PropTypes.shape({
    listings: PropTypes.shape({}),
  }),
  groupedFiles: PropTypes.shape({}),
  cottages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title_sv: PropTypes.string,
    }),
  ),
  isLoading: PropTypes.bool,
  isUploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  uploadFileIndex: PropTypes.number,
  uploadFileCount: PropTypes.number,
  isUpdating: PropTypes.bool,
  onUpload: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onReassign: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default withStyles(styles)(HostFilesList);
